import React, { useCallback, useEffect, useState } from "react";
import { Scale, Loader2, Plus, Trash2, RotateCcw, Save, Languages, FileDown, Eye, History, CalendarClock, Bell } from "lucide-react";
import DOMPurify from "dompurify";
import api from "../../api";
import { useLang } from "../../context/LanguageContext";
import { legalDefaults } from "../../config/legalDefaults";
import { useToast } from "../../hooks/use-toast";
import { RichText } from "./RichText";

const PAGES = [["terms", "Terms of Use"], ["privacy", "Privacy Policy"]];

const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const paras = (body) => String(body || "").split("\n\n").map((p) => `<p>${esc(p.trim())}</p>`).join("");
const toHtml = (intro, sections) =>
  (intro ? paras(intro) : "") +
  (sections || []).map((s) => `<h2>${esc(s.title)}</h2>${paras(s.body)}`).join("");

// Policies change with the business. Editing them shouldn't need a developer — or a translator.
export const AdminLegal = () => {
  const { toast } = useToast();
  const { t } = useLang();
  const [kind, setKind] = useState("terms");
  const [replace, setReplace] = useState(false);
  const [intro, setIntro] = useState("");
  const [updated, setUpdated] = useState("");
  const [sections, setSections] = useState([]);
  const [translated, setTranslated] = useState([]);
  const [busy, setBusy] = useState("");
  const [mode, setMode] = useState("sections");
  const [html, setHtml] = useState("");
  const [preview, setPreview] = useState(false);
  const [versions, setVersions] = useState([]);
  const [publishAt, setPublishAt] = useState("");
  const [notify, setNotify] = useState(false);
  const [scheduled, setScheduled] = useState(null);

  const loadSchedule = useCallback((k) => api.get(`/admin/legal/${k}/schedule`)
    .then(({ data }) => setScheduled(data.scheduled)).catch(() => setScheduled(null)), []);

  const loadVersions = useCallback((k) => api.get(`/admin/legal/${k}/versions`)
    .then(({ data }) => setVersions(data.versions || [])).catch(() => setVersions([])), []);

  const load = useCallback((k) => api.get(`/legal/${k}`).then(({ data }) => {
    setIntro(data.intro || ""); setUpdated(data.updated || "");
    setSections(data.sections || []); setTranslated(data.translated || []);
    setReplace(!!data.replace);
    setHtml(data.html || "");
    setMode(data.html ? "full" : "sections");
  }).catch(() => {}), []);
  useEffect(() => { load(kind); loadVersions(kind); loadSchedule(kind); }, [kind, load, loadVersions, loadSchedule]);

  const loadBuiltIn = () => {
    const base = legalDefaults(kind, t);
    setIntro(base.intro);
    setSections(base.sections.map((s) => ({ ...s })));
    setReplace(true);
    toast({ title: "Built-in text loaded", description: "Edit anything, then Save and publish to replace the page." });
  };

  const loadIntoEditor = () => {
    const base = legalDefaults(kind, t);
    const useOwn = replace && sections.length;
    const src = useOwn ? sections : [...base.sections, ...sections];
    setHtml(toHtml(intro || base.intro, src));
    toast({ title: "Loaded", description: "Edit it like a document, then Save and publish." });
  };

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.post(`/admin/legal/${kind}`, {
        intro, updated, replace,
        sections: sections.filter((s) => s.title.trim() && s.body.trim()),
        html: mode === "full" ? html : "",
        publishAt: publishAt ? new Date(publishAt).toISOString() : "",
        notify,
      });
      if (data.scheduled) {
        toast({ title: "Scheduled",
          description: `Goes live ${new Date(data.scheduled).toLocaleString()}${notify ? " and shops get told then." : "."}` });
        loadSchedule(kind);
      } else {
        toast({ title: "Published",
          description: data.notified
            ? `Live now, translating, and ${data.notified.emailed} shops have been told.`
            : "Live now, and being translated into ES, FR, PT and DE." });
        loadVersions(kind);
        setTimeout(() => load(kind), 12000);
      }
    } catch (e) { toast({ title: e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Could not save" }); }
    finally { setBusy(""); }
  };

  const cancelSchedule = async () => {
    setBusy("cancel");
    try {
      await api.post(`/admin/legal/${kind}/schedule/cancel`);
      setScheduled(null); setPublishAt("");
      toast({ title: "Schedule cancelled" });
    } catch { toast({ title: "Could not cancel" }); }
    finally { setBusy(""); }
  };

  const notifyNow = async () => {
    if (!window.confirm("Email every shop that this page has changed?")) return;
    setBusy("notify");
    try {
      const { data } = await api.post(`/admin/legal/${kind}/notify`);
      toast({ title: `Told ${data.recipients} shops`, description: `${data.emailed} emails went out.` });
    } catch (e) { toast({ title: e?.response?.data?.detail || "Could not send the notice" }); }
    finally { setBusy(""); }
  };

  const translate = async () => {
    setBusy("translate");
    try {
      const { data } = await api.post(`/admin/legal/${kind}/translate`);
      toast({ title: `Translated into ${data.languages.join(", ").toUpperCase()}` });
      load(kind);
    } catch (e) { toast({ title: e?.response?.data?.detail || "Could not translate" }); }
    finally { setBusy(""); }
  };

  const revert = async () => {
    if (!window.confirm("Drop your edits and go back to the built-in text?")) return;
    setBusy("revert");
    try {
      await api.post(`/admin/legal/${kind}/revert`);
      toast({ title: "Reverted to the built-in version" });
      setIntro(""); setUpdated(""); setSections([]); setTranslated([]);
      setHtml(""); setMode("sections");
      loadVersions(kind);
    } catch { toast({ title: "Could not revert" }); }
    finally { setBusy(""); }
  };

  const restore = async (v) => {
    if (!window.confirm(`Put the version saved on ${new Date(v.savedAt).toLocaleString()} back live?`)) return;
    setBusy(`restore-${v.id}`);
    try {
      await api.post(`/admin/legal/${kind}/restore/${v.id}`);
      toast({ title: "Restored", description: "That version is live and being re-translated." });
      load(kind); loadVersions(kind);
    } catch (e) { toast({ title: e?.response?.data?.detail || "Could not restore" }); }
    finally { setBusy(""); }
  };

  const previewHtml = () => {
    if (mode === "full") return html;
    const base = legalDefaults(kind, t);
    const src = replace && sections.length ? sections : [...base.sections, ...sections];
    return toHtml(intro || base.intro, src);
  };

  const setSec = (i, patch) => setSections((ss) => ss.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-legal">
      <h2 className="font-bold text-foreground flex items-center gap-2">
        <Scale size={18} className="text-[#16a34a]" /> Legal pages
      </h2>
      <p className="text-xs text-muted-foreground mt-0.5 mb-3">
        Your intro and your own clauses show on the live page under the built-in sections, and get
        translated automatically. Leave a field blank to keep the default.
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        {PAGES.map(([k, label]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`legal-page-${k}`}
            className={`text-xs font-bold rounded-full px-3 py-1.5 border transition-colors ${kind === k
              ? "bg-[#0f2a1a] text-white border-[#0f2a1a]"
              : "bg-card text-foreground border-border hover:border-[#16a34a]"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {[["sections", "Sections"], ["full", "Full text"]].map(([m, label]) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`legal-mode-${m}`}
            className={`text-xs font-bold rounded-full px-3 py-1.5 border transition-colors ${mode === m
              ? "bg-[#16a34a] text-white border-[#16a34a]"
              : "bg-card text-muted-foreground border-border hover:border-[#16a34a]"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {mode === "full" ? (
          <>
            <p className="text-xs text-muted-foreground">
              Full text wins: while there's anything in this editor, it is the whole page — the
              built-in sections and the ones below are not shown. Clear it to go back to sections.
            </p>
            <RichText value={html} onChange={setHtml} testid="legal-rich" />
            <button onClick={loadIntoEditor} data-testid="legal-load-into-editor"
              className="inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors">
              <FileDown size={13} /> Load the current text into the editor
            </button>
          </>
        ) : (
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Plain-English intro (replaces the default)</label>
          <textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={3} data-testid="legal-intro"
            className="mt-1 w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none resize-none" />
        </div>
        )}
        <div>
          <label className="text-xs font-semibold text-muted-foreground">“Last updated” label</label>
          <input value={updated} onChange={(e) => setUpdated(e.target.value)} data-testid="legal-updated"
            placeholder="e.g. October 2026"
            className="mt-1 w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none" />
        </div>

        {mode === "sections" && sections.map((s, i) => (
          <div key={i} className="border border-border rounded-xl p-3 space-y-2" data-testid={`legal-section-${i}`}>
            <div className="flex items-center gap-2">
              <input value={s.title} onChange={(e) => setSec(i, { title: e.target.value })} data-testid={`legal-section-title-${i}`}
                placeholder="Section title" className="flex-1 bg-secondary rounded-xl px-3 py-2 text-sm font-semibold outline-none" />
              <button onClick={() => setSections((ss) => ss.filter((_, j) => j !== i))} data-testid={`legal-section-remove-${i}`}
                className="text-rose-600 border border-rose-500/40 hover:bg-rose-500/10 rounded-xl p-2 transition-colors">
                <Trash2 size={14} />
              </button>
            </div>
            <textarea value={s.body} onChange={(e) => setSec(i, { body: e.target.value })} rows={4} data-testid={`legal-section-body-${i}`}
              placeholder="What it says, in plain words."
              className="w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none resize-none" />
          </div>
        ))}

        <div className="flex flex-wrap gap-2">
          {mode === "sections" && (
            <>
              <button onClick={loadBuiltIn} data-testid="legal-load-builtin"
                className="inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors">
                <FileDown size={13} /> Load the built-in text to edit
              </button>
              <button onClick={() => setSections((ss) => [...ss, { title: "", body: "" }])} data-testid="legal-add-section"
                className="inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors">
                <Plus size={13} /> Add a section
              </button>
            </>
          )}
          <button onClick={save} disabled={busy === "save"} data-testid="legal-save"
            className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white px-3 py-2 rounded-xl transition-colors">
            {busy === "save" ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />} {publishAt ? "Schedule this edit" : "Save and publish"}
          </button>
          <button onClick={translate} disabled={busy === "translate"} data-testid="legal-translate"
            className="inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors">
            {busy === "translate" ? <Loader2 className="animate-spin" size={13} /> : <Languages size={13} />} Re-translate now
          </button>
          <button onClick={() => setPreview((p) => !p)} data-testid="legal-preview-toggle"
            className="inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors">
            <Eye size={13} /> {preview ? "Hide preview" : "Preview the live page"}
          </button>
          <button onClick={revert} disabled={busy === "revert"} data-testid="legal-revert"
            className="inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-rose-500/50 px-3 py-2 rounded-xl transition-colors">
            {busy === "revert" ? <Loader2 className="animate-spin" size={13} /> : <RotateCcw size={13} />} Revert to default
          </button>
        </div>
        {translated.length > 0 && (
          <p className="text-xs text-muted-foreground" data-testid="legal-translated">
            Translated: {translated.join(", ").toUpperCase()}
          </p>
        )}
        {preview && (
          <div className="border border-border rounded-2xl overflow-hidden" data-testid="legal-preview">
            <div className="relative overflow-hidden bg-[#0f2a1a] text-white p-6">
              <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-[#16a34a]/40 blur-3xl" />
              <div className="relative">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-emerald-200">
                  {kind === "refunds" ? "Billing" : kind === "terms" ? "Legal" : "Your privacy"}
                </span>
                <h3 className="mt-2 text-2xl font-extrabold tracking-tight">
                  {PAGES.find(([k]) => k === kind)?.[1]}
                </h3>
                <p className="mt-1 text-xs text-emerald-100/80">
                  Last updated: {updated || "September 2026"}
                </p>
              </div>
            </div>
            <div className="lp-rich bg-card px-6 py-5 text-sm text-muted-foreground max-h-[60vh] overflow-y-auto lp-scroll"
              data-testid="legal-preview-body"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml()) }} />
            <p className="bg-secondary px-6 py-2 text-xs text-muted-foreground">
              This is exactly what shoppers will see once you hit Save and publish.
            </p>
          </div>
        )}

        {versions.length > 0 && (
          <div className="border border-border rounded-xl p-3" data-testid="legal-versions">
            <p className="text-xs font-bold text-foreground flex items-center gap-1.5 mb-2">
              <History size={13} className="text-[#16a34a]" /> Recent versions of this page
            </p>
            <div className="space-y-1.5">
              {versions.map((v) => (
                <div key={v.id} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`legal-version-${v.id}`}>
                  <span className="font-semibold text-foreground">{new Date(v.savedAt).toLocaleString()}</span>
                  <span className="text-muted-foreground">
                    {v.kindOfEdit} · {v.chars.toLocaleString()} chars{v.savedBy ? ` · ${v.savedBy}` : ""}
                  </span>
                  <button onClick={() => restore(v)} disabled={busy === `restore-${v.id}`}
                    data-testid={`legal-version-restore-${v.id}`}
                    className="ml-auto inline-flex items-center gap-1.5 font-bold border border-border hover:border-[#16a34a] px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-60">
                    {busy === `restore-${v.id}` ? <Loader2 className="animate-spin" size={12} /> : <RotateCcw size={12} />} Restore
                  </button>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              The last five saves are kept. Restoring one saves what's live now first, so you can undo the undo.
            </p>
          </div>
        )}

        <div className="border border-border rounded-xl p-3 space-y-2" data-testid="legal-publishing">
          <p className="text-xs font-bold text-foreground flex items-center gap-1.5">
            <CalendarClock size={13} className="text-[#16a34a]" /> Publishing
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground">Go live on (blank = now)</label>
              <input type="datetime-local" value={publishAt} onChange={(e) => setPublishAt(e.target.value)}
                data-testid="legal-publish-at"
                className="mt-1 bg-secondary rounded-xl px-3 py-2 text-sm text-foreground outline-none" />
            </div>
            {publishAt && (
              <button onClick={() => setPublishAt("")} data-testid="legal-publish-now"
                className="text-xs font-bold border border-border hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors">
                Publish now instead
              </button>
            )}
          </div>
          <label className="flex items-center gap-2 text-xs font-semibold text-foreground">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)}
              data-testid="legal-notify-toggle" className="h-4 w-4 accent-[#16a34a]" />
            Email every shop when this version goes live
          </label>
          <button onClick={notifyNow} disabled={busy === "notify"} data-testid="legal-notify-now"
            className="inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors disabled:opacity-60">
            {busy === "notify" ? <Loader2 className="animate-spin" size={13} /> : <Bell size={13} />} Tell shops about this page now
          </button>
          {scheduled && (
            <div className="flex flex-wrap items-center gap-2 text-xs bg-[#fdf6e3] dark:bg-[#3a2f10] border border-[#f5b942] rounded-xl px-3 py-2"
              data-testid="legal-scheduled-banner">
              <span className="font-bold text-foreground">
                An edit is waiting to go live on {new Date(scheduled.publishAt).toLocaleString()}
                {scheduled.notify ? " — shops get told then" : ""}
              </span>
              <button onClick={cancelSchedule} disabled={busy === "cancel"} data-testid="legal-schedule-cancel"
                className="ml-auto font-bold border border-border hover:border-rose-500/50 px-2.5 py-1.5 rounded-lg transition-colors">
                Cancel it
              </button>
            </div>
          )}
        </div>

        <label className="flex items-center gap-2 text-xs font-semibold text-foreground">
          <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)}
            data-testid="legal-replace-toggle" className="h-4 w-4 accent-[#16a34a]"
            disabled={mode === "full"} />
          My sections replace the built-in ones (rather than being added underneath)
        </label>
      </div>
    </div>
  );
};

export default AdminLegal;
